import {useCallback, useState} from 'react';
import {FormState, UserInterfaceState} from '@verihubs/pandora';
import {SystemEntity, useSystemModule, SubscribeEmailNotificationDTO} from '../../../domain/System';
import {useParams} from 'react-router-dom';
import {useSnackbar} from '@verihubs/pandora';

export const useViewModel = () => {
  const {
    SubscribeEmailNotificationUseCase,
    GetStatusUseCase,
    UnsubscribeEmailNotificationCallbackUseCase,
  } = useSystemModule();
  const SnackbarResponse = useSnackbar();
  const [uiState, setUiState] = useState<UserInterfaceState>(UserInterfaceState.Idle);
  const [systemStatus, setSystemStatus] = useState<SystemEntity[]>([]);
  const [formState, setFormState] = useState<FormState>(FormState.Idle);
  const [selected, setSelected] = useState<string[]>([]);
  const [modalState, setModalState] = useState<boolean>(true);
  const {id} = useParams();
  const onGetStatus = useCallback(() => {
    setUiState(UserInterfaceState.Loading);
    GetStatusUseCase()
      .then(result => {
        setUiState(UserInterfaceState.Idle);
        setSystemStatus(result);
      })
      .catch(() => {
        setUiState(UserInterfaceState.Error);
      });
  }, [GetStatusUseCase]);

  const onFormError = useCallback(() => {
    setFormState(FormState.Error);
    SnackbarResponse.error({
      message: 'Please enter a valid email and services that you wish to have updates sent to.',
    });
    return;
  }, []);

  const onSubmit = useCallback(
    (formData: SubscribeEmailNotificationDTO) => {
      setFormState(FormState.Submitting);
      SubscribeEmailNotificationUseCase(formData)
        .then(result => {
          if (result) {
            setFormState(FormState.Submitted);
            SnackbarResponse.success({
              message: 'Check your email inbox and confirm your subscription.',
            });
            return;
          }
          SnackbarResponse.success({message: 'Your service subscription has been updated.'});
        })
        .catch(() => {
          setFormState(FormState.Error);
          SnackbarResponse.error({message: 'Error'});
        });
    },
    [SubscribeEmailNotificationUseCase]
  );

  const onUnsubscribe = useCallback(() => {
    UnsubscribeEmailNotificationCallbackUseCase(id)
      .then(result => {
        if (result) {
          setModalState(false);
          SnackbarResponse.success({
            message:
              'You have been successfully unsubscribed and will not receive any further notifications.',
          });
          return;
        }
        setModalState(false);
        SnackbarResponse.error({message: 'You have already unsubscribed.'});
      })
      .catch(() => {
        setModalState(false);
        SnackbarResponse.error({message: 'Error.'});
      });
  }, [UnsubscribeEmailNotificationCallbackUseCase]);

  const handleChange = event => {
    const value = event.target.value;
    if (value === 'all') {
      setSelected(
        selected.length === systemStatus.map(ds => ds.service).length
          ? []
          : systemStatus.map(ds => ds.service)
      );
      return;
    }
    const list = [...selected];
    const index = list.indexOf(value);
    index === -1 ? list.push(value) : list.splice(index, 1);
    setSelected(list);
  };

  const handleCloseModal = (event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'backdropClick') {
      return;
    }

    setModalState(false);
  };
  return {
    data: {uiState, systemStatus, formState, selected, modalState},
    method: {
      onGetStatus,
      onSubmit,
      onFormError,
      handleChange,
      onUnsubscribe,
      handleCloseModal,
    },
  };
};
